import React, { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import CustomRichTextEditor from "../../../components/form/input/CustomRichTextEditor";
import { apiRequestWithAuth } from "../../../utils/api";
import Alert from "../../../components/ui/alert/Alert";
import PageMeta from "../../../components/common/PageMeta";
import PageBreadcrumb from "../../../components/common/PageBreadCrumb";
import ComponentCard from "../../../components/common/ComponentCard";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";
import FileInput from "../../../components/form/input/FileInput";

type SocialFormData = {
    title: string;
    shortDescription: string;
    postContent: string;
    orderNo: number;
    type: number;
    status: number;
    files: { key: string; fileId: any }[];
    link: string;
};

const defaultForm: SocialFormData = {
    title: "",
    shortDescription: "",
    postContent: "",
    orderNo: 1,
    type: 2,
    status: 1,
    files: [],
    link: "",
};

const EditSocials = () => {
    const { id } = useParams<{ id: string }>();
    const [formData, setFormData] = useState<SocialFormData>(defaultForm);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const navigate = useNavigate();

    // Fetch existing social
    useEffect(() => {
        const fetchSocial = async () => {
            try {
                const res = await apiRequestWithAuth(`/media/${id}`, "GET", {});
                const data = res.result;

                setFormData({
                    title: data.title,
                    shortDescription: data.shortDescription,
                    postContent: data.postContent,
                    orderNo: data.orderNo,
                    type: data.type,
                    status: data.status,
                    files: [],
                    link: data.link || "",
                });

                // Lấy ảnh đầu tiên nếu có
                const file = data.files?.find((f: any) => f.metaKey === "image");
                if (file && file.publicPath) {
                    setPreview(file.publicPath);
                }
            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu social:", error);
            }
        };

        if (id) fetchSocial();
    }, [id]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const uploadFile = async (file: File) => {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("id", "1");
        formDataUpload.append("subject", "quiz_cover");

        try {
            const res = await apiRequestWithAuth("/media/file", "POST", formDataUpload);
            return res.result?.id || null;
        } catch (error) {
            console.error("Upload failed", error);
            return null;
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            const uploadedId = await uploadFile(file);
            if (uploadedId) {
                setFormData((prev) => ({
                    ...prev,
                    files: [{ key: "image", fileId: uploadedId }],
                }));
            }
        }
    };

    const handleSave = async () => {
        try {
            await apiRequestWithAuth(`/media/${id}`, "PUT", formData);
            setOpenSnackbar(true);
            setTimeout(() => navigate("/socials"), 1000);
        } catch (error) {
            console.error("Failed to update social:", error);
        }
    };

    return (
        <div>
            {openSnackbar && (
                <Alert
                    variant="success"
                    title="Cập nhật social thành công"
                    message="Thông tin social đã được lưu."
                />
            )}

            <PageMeta title="Cập nhật social" description="Trang chỉnh sửa social" />
            <PageBreadcrumb pageTitle="Cập nhật social" />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                <div className="space-y-6">
                    <ComponentCard title="Thông tin social">
                        <div className="space-y-6">
                            <div>
                                <Label>Tiêu đề</Label>
                                <Input name="title" value={formData.title} onChange={handleChange}/>
                            </div>

                            <div>
                                <Label>Mô tả ngắn</Label>
                                <Input
                                    name="shortDescription"
                                    value={formData.shortDescription}
                                    onChange={handleChange}
                                />
                            </div>

                            <div>
                                <Label>Ảnh</Label>
                                <FileInput onChange={handleFileChange}/>
                                {preview && (
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        className="w-64 h-32 object-cover rounded border mt-2"
                                    />
                                )}
                            </div>

                            <div>
                                <Label>Trạng thái</Label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="1">Active</option>
                                    <option value="2">Draft</option>
                                    <option value="3">Inactive</option>
                                    <option value="4">Delete</option>
                                </select>
                            </div>

                            <div>
                                <Label>Thứ tự</Label>
                                <Input
                                    name="orderNo"
                                    type="number"
                                    value={formData.orderNo}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </ComponentCard>
                </div>

                <button
                    onClick={handleSave}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                >
                    Lưu thay đổi
                </button>
            </div>
        </div>
    );
};

export default EditSocials;
